/* One vocabulary for an upload's whole life, shared by every flow.
   Transitions are drawn out in docs/specs/media/media-upload-consolidation.md §2.2. */

export const STATUS = Object.freeze({
  IDLE: "idle",
  PREPARING: "preparing",
  UPLOADING: "uploading",
  CONFIRMING: "confirming",
  QUEUED: "queued",
  PROCESSING: "processing",
  COMPLETE: "complete",
  FAILED: "failed",
});

// While these hold, the bytes are leaving this device and closing the tab loses
// them. Everything after is the server's, and the page need not stay open.
const IN_FLIGHT = new Set([
  STATUS.PREPARING,
  STATUS.UPLOADING,
  STATUS.CONFIRMING,
]);

const SETTLING = new Set([STATUS.QUEUED, STATUS.PROCESSING]);

const TERMINAL = new Set([STATUS.COMPLETE, STATUS.FAILED]);

export const isInFlight = (status) => IN_FLIGHT.has(status);

export const isSettling = (status) => SETTLING.has(status);

export const isTerminal = (status) => TERMINAL.has(status);

export const isBusy = (status) => isInFlight(status) || isSettling(status);

export const isRetryable = ({ status, errorClassification }) =>
  status === STATUS.FAILED && errorClassification === "transient";

export const initialState = Object.freeze({
  status: STATUS.IDLE,
  uploadPercent: 0,
  processPercent: 0,
  stage: null,
  error: null,
  errorClassification: null,
  errorCode: null,
  errorLimit: null,
  errorDetails: null,
  mediaUploadId: null,
  processedKey: null,
  processedUrl: null,
});

const STARTED = "STARTED";
const UPLOAD_PERCENT = "UPLOAD_PERCENT";
const CONFIRMING = "CONFIRMING";
const QUEUED = "QUEUED";
const PROCESSING = "PROCESSING";
const COMPLETED = "COMPLETED";
const FAILED = "FAILED";
const RESET = "RESET";

export const started = () => ({ type: STARTED });

export const withUploadPercent = (percent) => ({
  type: UPLOAD_PERCENT,
  percent,
});

export const confirming = () => ({ type: CONFIRMING });

export const queued = (mediaUploadId) => ({ type: QUEUED, mediaUploadId });

export const withProcessing = ({ percent, stage }) => ({
  type: PROCESSING,
  percent,
  stage,
});

export const completed = ({ processedKey, processedUrl }) => ({
  type: COMPLETED,
  processedKey,
  processedUrl,
});

export const failed = ({
  error,
  errorClassification,
  errorCode,
  errorLimit,
  errorDetails,
}) => ({
  type: FAILED,
  error,
  errorClassification,
  errorCode,
  errorLimit,
  errorDetails,
});

export const reset = () => ({ type: RESET });

export const failureOf = ({ message, status, json }) => ({
  error: message,
  errorCode: json?.errorCode ?? (status === 0 ? "interrupted" : "unknown"),
  errorLimit: json?.details?.limit ?? null,
  errorDetails: json?.details ?? null,
});

const HANDLERS = {
  [STARTED]: () => ({ ...initialState, status: STATUS.PREPARING }),

  [UPLOAD_PERCENT]: (state, { percent }) => ({
    ...state,
    status: STATUS.UPLOADING,
    uploadPercent: percent,
  }),

  [CONFIRMING]: (state) => ({
    ...state,
    status: STATUS.CONFIRMING,
    uploadPercent: 100,
  }),

  // Named as a stage of its own so the wait for a worker slot reads as
  // "Waiting to start…" rather than a processing bar stuck at zero.
  [QUEUED]: (state, { mediaUploadId }) => ({
    ...state,
    status: STATUS.QUEUED,
    mediaUploadId: mediaUploadId ?? state.mediaUploadId,
    processPercent: 0,
    stage: "queued",
  }),

  [PROCESSING]: (state, { percent, stage }) => ({
    ...state,
    status: STATUS.PROCESSING,
    processPercent: percent === undefined ? state.processPercent : percent,
    stage,
  }),

  [COMPLETED]: (state, { processedKey, processedUrl }) => ({
    ...state,
    status: STATUS.COMPLETE,
    processPercent: 100,
    stage: "complete",
    processedKey: processedKey ?? null,
    processedUrl: processedUrl ?? null,
  }),

  [FAILED]: (
    state,
    { error, errorClassification, errorCode, errorLimit, errorDetails },
  ) => ({
    ...state,
    status: STATUS.FAILED,
    error,
    errorClassification: errorClassification ?? null,
    errorCode: errorCode ?? null,
    errorLimit: errorLimit ?? null,
    errorDetails: errorDetails ?? null,
  }),

  [RESET]: () => initialState,
};

// A completed upload absorbs nothing further: progress events can outlive the
// completion they preceded, and reviving the bar would undo the confetti. Only
// the two actions that mean "begin again" get through.
const RESTARTS = new Set([STARTED, RESET]);

const isIgnorable = (state, action) =>
  state.status === STATUS.COMPLETE && !RESTARTS.has(action.type);

export const reducer = (state, action) => {
  const handler = HANDLERS[action.type];

  return !handler || isIgnorable(state, action)
    ? state
    : handler(state, action);
};

// `validating` is a server-side status with no client meaning of its own: the
// worker has the job but has not reported progress yet.
const STATUS_FROM_SERVER = {
  queued: STATUS.QUEUED,
  validating: STATUS.QUEUED,
  processing: STATUS.PROCESSING,
  complete: STATUS.COMPLETE,
  failed: STATUS.FAILED,
};

// What the poll is for is the outcome a dropped socket swallowed, not the ticks
// in between — so it speaks up only when it has something the client has not
// already heard, and never moves the upload backwards.
export const reconcileAction = (state, snapshot) => {
  if (!snapshot || isTerminal(state.status)) return null;

  if (snapshot.status === STATUS.COMPLETE) {
    return completed(snapshot);
  }

  if (snapshot.status === STATUS.FAILED) {
    return failed({
      error: snapshot.error,
      errorClassification: snapshot.errorClassification,
      errorCode: snapshot.errorCode,
      errorLimit: snapshot.errorLimit,
    });
  }

  return snapshot.status === STATUS.PROCESSING && state.status === STATUS.QUEUED
    ? withProcessing({ percent: snapshot.processPercent, stage: null })
    : null;
};

export const fromStatusResponse = (response) => {
  const status = STATUS_FROM_SERVER[response?.status];

  if (!status) return null;

  return {
    status,
    mediaUploadId: response.mediaUploadId ?? null,
    processPercent: response.progress ?? 0,
    processedKey: response.s3ProcessedKey ?? null,
    processedUrl: response.processedUrl ?? null,
    error: response.errorMessage ?? null,
    errorClassification: response.errorClassification ?? null,
    errorCode: response.errorCode ?? null,
    errorLimit: response.errorLimit ?? null,
  };
};

const MiB = 1024 * 1024;

const formatBytes = (bytes) => `${Math.round(bytes / MiB)} MB`;

const plural = (count, unit) => `${count} ${unit}${count === 1 ? "" : "s"}`;

const formatDuration = (seconds) =>
  seconds % 60 === 0
    ? plural(seconds / 60, "minute")
    : plural(seconds, "second");

const AUDIO_TYPES = new Set(["audio_shoutout"]);

// Talent can change their camera's recording format; a fan recording a 15 s
// message in the browser cannot act on it.
const TALENT_VIDEO_TYPES = new Set(["vendor_video", "influencer_video_avatar"]);

const wordingFor = (uploadType) => ({
  noun: AUDIO_TYPES.has(uploadType) ? "recording" : "video",
  shorter: AUDIO_TYPES.has(uploadType) ? "recording" : "clip",
  advice: TALENT_VIDEO_TYPES.has(uploadType) ? ", or record at 1080p" : "",
});

const ERROR_COPY = Object.freeze({
  too_large: ({ noun, shorter, advice, errorLimit }) =>
    `This ${noun} is ${
      errorLimit ? `larger than ${formatBytes(errorLimit)}` : "too large"
    }. Try a shorter ${shorter}${advice}.`,
  too_long: ({ noun, errorLimit }) =>
    `This ${noun} is ${
      errorLimit ? `longer than ${formatDuration(errorLimit)}` : "too long"
    }. Trim it and try again.`,
  no_video: () => "This file has no video in it.",
  no_audio: ({ noun }) => `This ${noun} has no audio in it.`,
  timed_out: ({ noun, shorter, advice }) =>
    `This ${noun} took too long to process. Try a shorter ${shorter}${advice}.`,
  unreadable: () =>
    "This file couldn't be read. Try exporting it again, or record in the browser.",
  interrupted: () =>
    "The upload was interrupted. Check your connection and try again.",
  order_cancelled: () => "This order was cancelled.",
  already_processing: () => "Your last video is still processing.",
  already_signed: ({ errorDetails }) =>
    `${
      errorDetails?.influencerName ?? "The talent"
    } has already signed this item, so the video message can't be changed.`,
  unknown: () =>
    "Something went wrong with this upload. Try again, or contact support if it keeps happening.",
});

export const uploadErrorCopy = ({
  errorCode,
  errorLimit,
  errorDetails,
  uploadType,
}) =>
  (Object.hasOwn(ERROR_COPY, errorCode ?? "")
    ? ERROR_COPY[errorCode]
    : ERROR_COPY.unknown)({
    ...wordingFor(uploadType),
    errorLimit,
    errorDetails,
  });
